/**
 * Reverse-geocoding helper for the Group Share host
 * (StartGroupShareButton / useStartGroupShare).
 *
 * The QR scan page does not use this module. It needs a fresh
 * high-accuracy fix with no cached or IP fallback. See qrScanLocation.js.
 *
 * Session 124l rewrite: previous implementation relied on
 * BigDataCloud's `localityInfo.informative` bucket which frequently
 * surfaced timezone names ("America/Los_Angeles") and very broad
 * admin regions instead of a recognisable building, defeating the
 * point of the auto-fill. We now go straight to OpenStreetMap
 * Nominatim with `zoom=18` (building-level) + `addressdetails=1` and
 * pick the most specific *named* place from the response. BigDataCloud
 * is kept as a graceful fallback only when Nominatim is unreachable.
 *
 * Returns a short human-readable label like:
 *   "Sightglass Coffee, San Francisco"
 *   "375 Valencia St, San Francisco"   (no named POI nearby)
 *   "Mountain View, California"        (no street-level match)
 *
 * No API key required.
 *
 * Future swap: when a Google Maps API key is available, the only
 * change needed is to point `_PROVIDERS[provider]` at a Google
 * Places Nearby Search call returning the closest `establishment`.
 * The function signature and return contract stay identical.
 */

// Locality fields we treat as "the city". First non-empty wins.
const CITY_KEYS = ['city', 'town', 'village', 'hamlet', 'suburb', 'neighbourhood'];
const REGION_KEYS = ['state', 'province', 'region'];

// Address keys that are ACTUAL named places (in priority order).
// We pick the first non-empty one. `amenity` covers restaurants,
// cafés, banks, schools, etc.; `shop` covers retail; `office`,
// `building`, `tourism`, `leisure`, `historic`, `industrial` cover
// most other commercial/residential structures.
const VENUE_KEYS = [
  'amenity', 'shop', 'tourism', 'leisure', 'historic', 'office',
  'building', 'industrial', 'craft', 'club', 'public_building',
];

// Reject these as venue names — they're admin regions, never places.
// Without this filter Nominatim sometimes returns e.g. the road name
// in `amenity` for unnamed parks.
const _LOOKS_LIKE_TIMEZONE = /^(?:[A-Z][a-z]+\/[A-Za-z_]+|UTC[+-]\d|GMT[+-]\d)/;


function _pickFirst(obj, keys) {
  for (const k of keys) {
    const v = (obj?.[k] || '').toString().trim();
    if (v) return v;
  }
  return '';
}

function _buildLabel(addr, displayName, topName = '') {
  // 1) Named venue — Nominatim's top-level `name` (canonical venue
  //    label like "Craft Beer Market") wins over the address.* keys.
  let venue = topName || _pickFirst(addr, VENUE_KEYS);
  if (venue && (_LOOKS_LIKE_TIMEZONE.test(venue) || venue === (addr?.road || ''))) venue = '';

  // 2) Street address fallback — house_number + road. Useful for
  //    residences and any address that doesn't tag a named POI.
  const houseNo = (addr?.house_number || '').trim();
  const road = (addr?.road || addr?.pedestrian || addr?.footway || '').trim();
  const street = [houseNo, road].filter(Boolean).join(' ');

  const city = _pickFirst(addr, CITY_KEYS);
  const region = _pickFirst(addr, REGION_KEYS);

  if (venue && city)  return `${venue}, ${city}`;
  if (venue)          return venue;
  if (street && city) return `${street}, ${city}`;
  if (street)         return street;
  if (city && region && city !== region) return `${city}, ${region}`;
  if (city)           return city;

  // Last resort — first comma-segment of Nominatim's display_name.
  if (displayName) return displayName.split(',')[0].trim();
  return '';
}


async function _fetchNominatimReverse(latitude, longitude, extra = '') {
  const url = (
    'https://nominatim.openstreetmap.org/reverse'
    + `?lat=${latitude}&lon=${longitude}`
    + '&format=jsonv2&addressdetails=1&zoom=18'
    + extra
  );
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      // Nominatim ToS asks for an identifying UA.
      Accept: 'application/json',
    },
  });
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  return res.json();
}

function _distMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function _reverseGeocodeNominatim(latitude, longitude) {
  const data = await _fetchNominatimReverse(latitude, longitude);
  const addr = data?.address || {};
  const topName = (data?.name || '').trim();
  const label = _buildLabel(addr, data?.display_name || '', topName);

  // Session 127 — venue-name-first. If the primary lookup produced a
  // bare street address ("345 10 Ave SW"), ask the POI layer for the
  // nearest NAMED place and prefer it when within ~120m of the fix
  // ("Craft on 10th"). Best-effort: any failure keeps the street label.
  const hasVenue = !!(topName && topName !== (addr.road || ''))
    || !!_pickFirst(addr, VENUE_KEYS);
  if (!hasVenue) {
    try {
      const poi = await _fetchNominatimReverse(latitude, longitude, '&layer=poi');
      const poiName = (poi?.name || '').trim();
      if (poiName && !_LOOKS_LIKE_TIMEZONE.test(poiName) && poi?.lat && poi?.lon) {
        const d = _distMeters(latitude, longitude, parseFloat(poi.lat), parseFloat(poi.lon));
        if (Number.isFinite(d) && d <= 120) {
          const city = _pickFirst(poi?.address || {}, CITY_KEYS) || _pickFirst(addr, CITY_KEYS);
          return city && poiName.toLowerCase() !== city.toLowerCase()
            ? `${poiName}, ${city}`
            : poiName;
        }
      }
    } catch { /* keep street-address label */ }
  }
  return label;
}


async function _reverseGeocodeBigDataCloud(latitude, longitude) {
  // Fallback only — used if Nominatim is down. BigDataCloud's free
  // `reverse-geocode-client` endpoint gives city + region without
  // building granularity.
  const res = await fetch(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
  );
  if (!res.ok) throw new Error(`bigdatacloud ${res.status}`);
  const data = await res.json();
  const city = data?.locality || data?.city || '';
  const region = data?.principalSubdivision || '';
  if (city && region && city !== region) return `${city}, ${region}`;
  return city || region || '';
}


// Bare GPS fix for callers that resolve the place name server-side.
// maximumAge is 60s here, so this can return a cached position. The QR
// scan page must not call it. Use readPreciseScanPosition instead.
export async function getCurrentCoords(opts = {}) {
  const { timeoutMs = 10000 } = opts;
  if (!('geolocation' in navigator)) {
    throw new Error('Location not supported on this device');
  }
  const position = await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Location request timed out')), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(t); resolve(pos); },
      (err) => {
        clearTimeout(t);
        const msg = err.code === 1 ? 'Location permission denied' : (err.message || 'Could not get your location');
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: timeoutMs },
    );
  });
  return { lat: position.coords.latitude, lng: position.coords.longitude };
}


/** A pin is saved only for a fresh, accurate fix. A label can still be kept. */
export function groupSessionPin(position, now = Date.now()) {
  const coords = position?.coords || {};
  const lat = coords.latitude;
  const lng = coords.longitude;
  const accuracy = coords.accuracy;
  const timestamp = position?.timestamp;
  const numbers = [lat, lng, accuracy, timestamp];
  if (!numbers.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (accuracy < 0 || accuracy > 50) return null;
  if (timestamp > now || now - timestamp > 60_000) return null;
  return { lat, lng };
}

export async function readGroupSessionStart(opts = {}) {
  const { timeoutMs = 10000 } = opts;
  if (!('geolocation' in navigator)) {
    throw new Error('Location not supported on this device');
  }
  const position = await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Location request timed out')), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(t); resolve(pos); },
      (err) => {
        clearTimeout(t);
        const msg = err.code === 1 ? 'Location permission denied' : (err.message || 'Could not get your location');
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs },
    );
  });
  const geo = groupSessionPin(position);
  const { latitude, longitude } = position.coords;
  let label = '';
  try {
    label = await _reverseGeocodeNominatim(latitude, longitude);
  } catch {
    label = '';
  }
  if (!label) {
    try {
      label = await _reverseGeocodeBigDataCloud(latitude, longitude);
    } catch {
      label = '';
    }
  }
  return { label: label || '', geo };
}

export async function getCurrentPlaceLabel(opts = {}) {
  const { timeoutMs = 10000 } = opts;

  if (!('geolocation' in navigator)) {
    throw new Error('Location not supported on this device');
  }
  const position = await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Location request timed out')), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(t); resolve(pos); },
      (err) => {
        clearTimeout(t);
        const msg = err.code === 1 ? 'Location permission denied' : (err.message || 'Could not get your location');
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: timeoutMs },
    );
  });

  const { latitude, longitude } = position.coords;
  try {
    const label = await _reverseGeocodeNominatim(latitude, longitude);
    if (label) return label;
  } catch {
    // fall through
  }

  // 2) BigDataCloud — coarse-grained fallback.
  try {
    const label = await _reverseGeocodeBigDataCloud(latitude, longitude);
    if (label) return label;
  } catch {
    // fall through
  }

  // 3) Raw coordinates — last resort.
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}


// Exported for unit testing — the label-building logic is the part
// that materially changes between providers and is worth covering.
export const __test = { _buildLabel };
